import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import settingsService, {
  UserProfile,
  NotificationPreferences,
  FirmSettings,
  ChangePasswordInput,
} from '../../services/settingsService';
import { getApiErrorMessage } from '../../utils/apiError';

interface SettingsState {
  profile: UserProfile | null;
  notifications: NotificationPreferences | null;
  firm: FirmSettings | null;
  loading: boolean;
  error: string | null;
  profileLoading: boolean;
  notificationsLoading: boolean;
  firmLoading: boolean;
  passwordLoading: boolean;
  twoFactorLoading: boolean;
  avatarUploading: boolean;
  logoUploading: boolean;
  twoFactorEnabled: boolean;
}

const initialState: SettingsState = {
  profile: null,
  notifications: null,
  firm: null,
  loading: false,
  error: null,
  profileLoading: false,
  notificationsLoading: false,
  firmLoading: false,
  passwordLoading: false,
  twoFactorLoading: false,
  avatarUploading: false,
  logoUploading: false,
  twoFactorEnabled: false,
};

// Profile async thunks
export const getProfile = createAsyncThunk(
  'settings/getProfile',
  async (userId: string, { rejectWithValue }) => {
    try {
      const response = await settingsService.getProfile(userId);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to get profile'));
    }
  }
);

export const updateProfile = createAsyncThunk(
  'settings/updateProfile',
  async ({ userId, data }: { userId: string; data: Partial<UserProfile> }, { rejectWithValue }) => {
    try {
      const response = await settingsService.updateProfile(userId, data);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to update profile'));
    }
  }
);

export const uploadAvatar = createAsyncThunk(
  'settings/uploadAvatar',
  async ({ userId, file }: { userId: string; file: FormData }, { rejectWithValue }) => {
    try {
      const response = await settingsService.uploadAvatar(userId, file);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to upload avatar'));
    }
  }
);

// Notifications async thunks
export const getNotificationPreferences = createAsyncThunk(
  'settings/getNotificationPreferences',
  async (userId: string, { rejectWithValue }) => {
    try {
      const response = await settingsService.getNotificationPreferences(userId);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to get notification preferences'));
    }
  }
);

export const updateNotificationPreferences = createAsyncThunk(
  'settings/updateNotificationPreferences',
  async ({ userId, data }: { userId: string; data: NotificationPreferences }, { rejectWithValue }) => {
    try {
      const response = await settingsService.updateNotificationPreferences(userId, data);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to update notification preferences'));
    }
  }
);

// Security async thunks
export const changePassword = createAsyncThunk(
  'settings/changePassword',
  async ({ userId, data }: { userId: string; data: ChangePasswordInput }, { rejectWithValue }) => {
    try {
      const response = await settingsService.changePassword(userId, data);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to change password'));
    }
  }
);

export const enable2FA = createAsyncThunk(
  'settings/enable2FA',
  async (userId: string, { rejectWithValue }) => {
    try {
      const response = await settingsService.enable2FA(userId);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to enable 2FA'));
    }
  }
);

export const disable2FA = createAsyncThunk(
  'settings/disable2FA',
  async (userId: string, { rejectWithValue }) => {
    try {
      const response = await settingsService.disable2FA(userId);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to disable 2FA'));
    }
  }
);

export const verify2FA = createAsyncThunk(
  'settings/verify2FA',
  async ({ userId, code }: { userId: string; code: string }, { rejectWithValue }) => {
    try {
      const response = await settingsService.verify2FA(userId, code);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to verify 2FA code'));
    }
  }
);

// Firm settings async thunks
export const getFirmSettings = createAsyncThunk(
  'settings/getFirmSettings',
  async (_, { rejectWithValue }) => {
    try {
      const response = await settingsService.getFirmSettings();
      return response.data;
    } catch (error: any) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to get firm settings'));
    }
  }
);

export const updateFirmSettings = createAsyncThunk(
  'settings/updateFirmSettings',
  async (data: Partial<FirmSettings>, { rejectWithValue }) => {
    try {
      const response = await settingsService.updateFirmSettings(data);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to update firm settings'));
    }
  }
);

export const uploadFirmLogo = createAsyncThunk(
  'settings/uploadFirmLogo',
  async (file: FormData, { rejectWithValue }) => {
    try {
      const response = await settingsService.uploadFirmLogo(file);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to upload firm logo'));
    }
  }
);

const settingsSlice = createSlice({
  name: 'settings',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    resetState: (state) => {
      state.profile = null;
      state.notifications = null;
      state.firm = null;
      state.loading = false;
      state.error = null;
      state.profileLoading = false;
      state.notificationsLoading = false;
      state.firmLoading = false;
      state.passwordLoading = false;
      state.twoFactorLoading = false;
      state.avatarUploading = false;
      state.logoUploading = false;
      state.twoFactorEnabled = false;
    },
  },
  extraReducers: (builder) => {
    builder
      // Get profile
      .addCase(getProfile.pending, (state) => {
        state.profileLoading = true;
        state.error = null;
      })
      .addCase(getProfile.fulfilled, (state, action) => {
        state.profileLoading = false;
        state.profile = action.payload;
        state.error = null;
      })
      .addCase(getProfile.rejected, (state, action) => {
        state.profileLoading = false;
        state.error = action.payload as string;
      })

      // Update profile
      .addCase(updateProfile.pending, (state) => {
        state.profileLoading = true;
        state.error = null;
      })
      .addCase(updateProfile.fulfilled, (state, action) => {
        state.profileLoading = false;
        state.profile = action.payload;
        state.error = null;
      })
      .addCase(updateProfile.rejected, (state, action) => {
        state.profileLoading = false;
        state.error = action.payload as string;
      })

      // Upload avatar
      .addCase(uploadAvatar.pending, (state) => {
        state.avatarUploading = true;
        state.error = null;
      })
      .addCase(uploadAvatar.fulfilled, (state, action) => {
        state.avatarUploading = false;
        if (state.profile) {
          // Backend has been observed returning either key — fall back so
          // the preview on this page actually updates instead of staying
          // stale until the next full profile refetch.
          state.profile.avatar = action.payload.avatar_url ?? action.payload.avatar;
        }
        state.error = null;
      })
      // Note: the logged-in user's own avatar (shown in the sidebar/header)
      // lives in authSlice, not here — see authSlice's extraReducer on this
      // same uploadAvatar.fulfilled action for the header/sidebar update.
      .addCase(uploadAvatar.rejected, (state, action) => {
        state.avatarUploading = false;
        state.error = action.payload as string;
      })

      // Get notification preferences
      .addCase(getNotificationPreferences.pending, (state) => {
        state.notificationsLoading = true;
        state.error = null;
      })
      .addCase(getNotificationPreferences.fulfilled, (state, action) => {
        state.notificationsLoading = false;
        state.notifications = action.payload;
        state.error = null;
      })
      .addCase(getNotificationPreferences.rejected, (state, action) => {
        state.notificationsLoading = false;
        state.error = action.payload as string;
      })

      // Update notification preferences
      .addCase(updateNotificationPreferences.pending, (state) => {
        state.notificationsLoading = true;
        state.error = null;
      })
      .addCase(updateNotificationPreferences.fulfilled, (state, action) => {
        state.notificationsLoading = false;
        state.notifications = action.payload;
        state.error = null;
      })
      .addCase(updateNotificationPreferences.rejected, (state, action) => {
        state.notificationsLoading = false;
        state.error = action.payload as string;
      })

      // Change password
      .addCase(changePassword.pending, (state) => {
        state.passwordLoading = true;
        state.error = null;
      })
      .addCase(changePassword.fulfilled, (state) => {
        state.passwordLoading = false;
        state.error = null;
      })
      .addCase(changePassword.rejected, (state, action) => {
        state.passwordLoading = false;
        state.error = action.payload as string;
      })

      // Enable 2FA
      .addCase(enable2FA.pending, (state) => {
        state.twoFactorLoading = true;
        state.error = null;
      })
      .addCase(enable2FA.fulfilled, (state) => {
        state.twoFactorLoading = false;
        state.twoFactorEnabled = true;
        state.error = null;
      })
      .addCase(enable2FA.rejected, (state, action) => {
        state.twoFactorLoading = false;
        state.error = action.payload as string;
      })

      // Disable 2FA
      .addCase(disable2FA.pending, (state) => {
        state.twoFactorLoading = true;
        state.error = null;
      })
      .addCase(disable2FA.fulfilled, (state) => {
        state.twoFactorLoading = false;
        state.twoFactorEnabled = false;
        state.error = null;
      })
      .addCase(disable2FA.rejected, (state, action) => {
        state.twoFactorLoading = false;
        state.error = action.payload as string;
      })

      // Verify 2FA
      .addCase(verify2FA.pending, (state) => {
        state.twoFactorLoading = true;
        state.error = null;
      })
      .addCase(verify2FA.fulfilled, (state) => {
        state.twoFactorLoading = false;
        state.error = null;
      })
      .addCase(verify2FA.rejected, (state, action) => {
        state.twoFactorLoading = false;
        state.error = action.payload as string;
      })

      // Get firm settings
      .addCase(getFirmSettings.pending, (state) => {
        state.firmLoading = true;
        state.error = null;
      })
      .addCase(getFirmSettings.fulfilled, (state, action) => {
        state.firmLoading = false;
        state.firm = action.payload;
        state.error = null;
      })
      .addCase(getFirmSettings.rejected, (state, action) => {
        state.firmLoading = false;
        state.error = action.payload as string;
      })

      // Update firm settings
      .addCase(updateFirmSettings.pending, (state) => {
        state.firmLoading = true;
        state.error = null;
      })
      .addCase(updateFirmSettings.fulfilled, (state, action) => {
        state.firmLoading = false;
        state.firm = action.payload;
        state.error = null;
      })
      .addCase(updateFirmSettings.rejected, (state, action) => {
        state.firmLoading = false;
        state.error = action.payload as string;
      })

      // Upload firm logo
      .addCase(uploadFirmLogo.pending, (state) => {
        state.logoUploading = true;
        state.error = null;
      })
      .addCase(uploadFirmLogo.fulfilled, (state, action) => {
        state.logoUploading = false;
        if (state.firm) {
          state.firm.logo_url = action.payload.logo_url;
        }
        state.error = null;
      })
      .addCase(uploadFirmLogo.rejected, (state, action) => {
        state.logoUploading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearError, resetState } = settingsSlice.actions;
export default settingsSlice.reducer;
